import * as Y from 'yjs';
import {
  type BoardElement,
  type BoardMeta,
  type Frame,
  normalizeElement,
  normalizeFrame,
  type TimerState,
} from './elements';
import { compareFrames, compareOrdered } from './order';

/*
 * Layout of a board's Y.Doc:
 *
 *   meta      Y.Map    title, timer
 *   frames    Y.Map    frameId   -> Y.Map { id, order, name, background }
 *   elements  Y.Map    elementId -> Y.Map { id, type, frameId, z, x, y, ... }
 *
 * Each element is its own Y.Map so that two people changing different
 * properties of the same element (one moves it, another recolors it) both win.
 * Elements live in one flat map with a frameId rather than nested per frame,
 * which keeps lookups by id and moving elements between frames trivial.
 */

export type YRecord = Y.Map<unknown>;

export function metaMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap<unknown>('meta');
}

export function framesMap(doc: Y.Doc): Y.Map<YRecord> {
  return doc.getMap<YRecord>('frames');
}

export function elementsMap(doc: Y.Doc): Y.Map<YRecord> {
  return doc.getMap<YRecord>('elements');
}

export const DEFAULT_BOARD_TITLE = 'Untitled board';
/** Longest board title; anything longer written to the document is cut here. */
export const MAX_TITLE_LENGTH = 120;

// ---------------------------------------------------------------------------
// Elements
// ---------------------------------------------------------------------------

/** Reads an element's Y.Map into a plain object (nested maps such as votes become records). */
export function readElement(map: YRecord): BoardElement | null {
  const raw: Record<string, unknown> = {};
  for (const [key, value] of map.entries()) {
    raw[key] = value instanceof Y.Map ? Object.fromEntries(value.entries()) : value;
  }
  return normalizeElement(raw);
}

function writeFields(map: YRecord, el: BoardElement): void {
  for (const [key, value] of Object.entries(el)) {
    if (value === undefined) continue;
    if (key === 'votes') {
      const votes = new Y.Map<boolean>();
      map.set('votes', votes);
      for (const voter of Object.keys(value as Record<string, true>)) votes.set(voter, true);
    } else {
      map.set(key, value);
    }
  }
}

/** Adds an element to the document. Call inside a transaction. */
export function insertElement(doc: Y.Doc, el: BoardElement): void {
  const map = new Y.Map<unknown>();
  elementsMap(doc).set(el.id, map);
  writeFields(map, el);
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a instanceof Uint8Array && b instanceof Uint8Array) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }
  if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

/**
 * Writes only the properties that actually change, so concurrent edits to other
 * properties are not overwritten and no-op updates are not sent.
 * Returns whether anything changed.
 */
export function patchRecord(map: YRecord, patch: Record<string, unknown>): boolean {
  let changed = false;
  for (const [key, value] of Object.entries(patch)) {
    if (key === 'id' || key === 'type' || key === 'votes') continue;
    if (value === undefined) {
      if (map.has(key)) {
        map.delete(key);
        changed = true;
      }
      continue;
    }
    if (!valuesEqual(map.get(key), value)) {
      map.set(key, value);
      changed = true;
    }
  }
  return changed;
}

/** Adds or removes `voterId`'s vote on a sticky. Returns true if the vote is now cast. */
export function toggleVote(map: YRecord, voterId: string): boolean {
  let votes = map.get('votes');
  if (!(votes instanceof Y.Map)) {
    votes = new Y.Map<boolean>();
    map.set('votes', votes);
  }
  const typed = votes as Y.Map<boolean>;
  if (typed.has(voterId)) {
    typed.delete(voterId);
    return false;
  }
  typed.set(voterId, true);
  return true;
}

// ---------------------------------------------------------------------------
// Frames and metadata
// ---------------------------------------------------------------------------

export function readFrame(map: YRecord): Frame | null {
  return normalizeFrame(Object.fromEntries(map.entries()));
}

/** Adds a frame to the document. Call inside a transaction. */
export function insertFrame(doc: Y.Doc, frame: Frame): void {
  const map = new Y.Map<unknown>();
  framesMap(doc).set(frame.id, map);
  for (const [key, value] of Object.entries(frame)) map.set(key, value);
}

function isTimerState(value: unknown): value is TimerState {
  if (typeof value !== 'object' || value === null) return false;
  const t = value as Record<string, unknown>;
  return (
    typeof t.durationMs === 'number' &&
    typeof t.remainingMs === 'number' &&
    (t.endsAt === null || typeof t.endsAt === 'number')
  );
}

export function readMeta(doc: Y.Doc): BoardMeta {
  const meta = metaMap(doc);
  const title = meta.get('title');
  const timer = meta.get('timer');
  return {
    title:
      typeof title === 'string' && title.trim()
        ? title.slice(0, MAX_TITLE_LENGTH)
        : DEFAULT_BOARD_TITLE,
    timer: isTimerState(timer) ? timer : null,
  };
}

// ---------------------------------------------------------------------------
// Whole-board content
// ---------------------------------------------------------------------------

export interface BoardContent {
  frames: Frame[];
  elements: BoardElement[];
}

function notNull<T>(value: T | null): value is T {
  return value !== null;
}

/** Plain snapshot of every frame and element, frames in strip order and elements in paint order. */
export function readBoardContent(doc: Y.Doc): BoardContent {
  const frames = [...framesMap(doc).values()].map(readFrame).filter(notNull).sort(compareFrames);
  const elements = [...elementsMap(doc).values()]
    .map(readElement)
    .filter(notNull)
    .sort(compareOrdered);
  return { frames, elements };
}

/** Adds frames and elements to the document. Call inside a transaction. */
export function insertBoardContent(doc: Y.Doc, content: BoardContent): void {
  for (const frame of content.frames) insertFrame(doc, frame);
  for (const el of content.elements) insertElement(doc, el);
}

/** Removes every frame and element. Call inside a transaction. */
export function clearBoardContent(doc: Y.Doc): void {
  framesMap(doc).clear();
  elementsMap(doc).clear();
}

/** Fills a brand-new document with a title and its initial content. */
export function initBoardDoc(doc: Y.Doc, title: string, content: BoardContent): void {
  doc.transact(() => {
    metaMap(doc).set('title', title);
    insertBoardContent(doc, content);
  });
}

/** Replaces all frames and elements, e.g. when restoring a version. Keeps the title. */
export function replaceBoardContent(doc: Y.Doc, content: BoardContent, origin?: unknown): void {
  doc.transact(() => {
    clearBoardContent(doc);
    insertBoardContent(doc, content);
  }, origin);
}

/**
 * Deep-copies content with fresh ids for every frame and element, keeping all
 * internal references (frame membership, connector attachments, groups) intact.
 * References to elements outside the copied set are dropped; connectors keep
 * their stored endpoint position instead, so bake endpoints first if needed.
 */
export function cloneContentWithNewIds(content: BoardContent, newId: () => string): BoardContent {
  const frameIds = new Map(content.frames.map((f) => [f.id, newId()]));
  const elementIds = new Map(content.elements.map((e) => [e.id, newId()]));
  const groupIds = new Map<string, string>();
  const frames = content.frames.map((f) => ({
    ...f,
    id: frameIds.get(f.id) as string,
    background: { ...f.background },
  }));
  const elements = content.elements.map((original) => {
    const el = structuredClone(original);
    el.id = elementIds.get(original.id) as string;
    el.frameId = frameIds.get(original.frameId) ?? original.frameId;
    if (el.groupId) {
      let group = groupIds.get(el.groupId);
      if (!group) {
        group = newId();
        groupIds.set(el.groupId, group);
      }
      el.groupId = group;
    }
    if (el.type === 'connector') {
      for (const end of [el.start, el.end]) {
        if (!end.bindId) continue;
        const mapped = elementIds.get(end.bindId);
        end.bindId = mapped ?? null;
        if (!mapped) end.anchor = null;
      }
    }
    return el;
  });
  return { frames, elements };
}
